// Doyle 2016 (Fertil Steril 2016;105:459-466, PMID 26604065) reports per-oocyte
// live-birth "efficiency" by age band from a cohort of vitrified/warmed
// autologous cycles. We translate the efficiency into P(>=k live births) with
// a Poisson approximation: mean births = efficiency * mature oocytes.

export type DoyleAgeBand = "30-34" | "35-37" | "38-40" | "41-42";

const DOYLE_EFFICIENCY: Record<DoyleAgeBand, number> = {
  "30-34": 0.087,
  "35-37": 0.073,
  "38-40": 0.045,
  "41-42": 0.025,
};

const DOYLE_LOWER_BOUND = 30;
const DOYLE_UPPER_BOUND = 42;

export const DOYLE_SOURCE_ID = "doyle-vitrified-oocyte-2016";

function ageBandFor(age: number): DoyleAgeBand | null {
  if (age < DOYLE_LOWER_BOUND || age > DOYLE_UPPER_BOUND) return null;
  if (age <= 34) return "30-34";
  if (age <= 37) return "35-37";
  if (age <= 40) return "38-40";
  return "41-42";
}

export type DoyleLiveBirthEstimate = {
  ageBand: DoyleAgeBand;
  matureOocytes: number;
  expectedLiveBirths: number;
  probabilityAtLeastOne: number;
  probabilityAtLeastTwo: number;
  probabilityAtLeastThree: number;
};

export function doyleLiveBirthEstimate(
  age: number,
  matureOocytes: number,
): DoyleLiveBirthEstimate | null {
  const band = ageBandFor(age);
  if (band == null) return null;
  if (!Number.isFinite(matureOocytes) || matureOocytes < 0) return null;
  const lambda = DOYLE_EFFICIENCY[band] * matureOocytes;
  const pAtLeastOne = 1 - Math.exp(-lambda);
  const pAtLeastTwo = 1 - Math.exp(-lambda) * (1 + lambda);
  const pAtLeastThree =
    1 - Math.exp(-lambda) * (1 + lambda + (lambda * lambda) / 2);
  return {
    ageBand: band,
    matureOocytes,
    expectedLiveBirths: lambda,
    probabilityAtLeastOne: clamp01(pAtLeastOne),
    probabilityAtLeastTwo: clamp01(pAtLeastTwo),
    probabilityAtLeastThree: clamp01(pAtLeastThree),
  };
}

export function doyleAgeRange(): { min: number; max: number } {
  return { min: DOYLE_LOWER_BOUND, max: DOYLE_UPPER_BOUND };
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}
